import React, {Component} from 'react';
import {
    IModelApiResponseViewObject,
    IModelDictionaryDatum,
    Loader,
    LoaderType,
    Pagination,
    Price,
    Tooltip,
    Translation,
    withRouterWrapper,
    WithRouterWrapperProps,
} from 'jobhunter-common-web';
import LayoutWrapper from '../LayoutWrapper';
import {Badge, Button, Card, CardBody, Table} from 'reactstrap';
import {CheckSquare, Search} from 'react-feather';
import {WithTranslation, withTranslation} from 'react-i18next';
import ServiceFilters from './ServiceFilters';
import {
    acceptService,
    applyServiceFilters,
    changeServiceFilters,
    changeServicesPagination,
    fetchServicesList,
    IPaginationParams,
    IServiceFilters,
    resetToInitialServicePageState,
} from '../../store/reducers/servicesPageSlice';
import {IModelServiceListItem} from '../../model/servicesDataModel';
import {connect} from 'react-redux';
import {RootState} from '../../store/reducers';
import {
    isServicesPageLoading,
    serviceFiltersSelector,
    servicesListSelector,
    servicesMetadataSelector,
    servicesPaginationSelector,
} from '../../store/selectors/servicesPageSelectors';
import AcceptService from './AcceptService';

declare type WithRouterProps = typeof WithRouterWrapperProps;

interface IServicesProps extends WithTranslation, WithRouterProps {
    readonly isLoading: boolean;
    readonly servicesList: IModelServiceListItem[] | null;
    readonly serviceFilters: IServiceFilters | null;
    readonly servicesMetadata: typeof IModelApiResponseViewObject | null;
    readonly pagination: IPaginationParams;
    readonly fetchServicesList: typeof fetchServicesList;
    readonly resetToInitialServicePageState: typeof resetToInitialServicePageState;
    readonly acceptService: typeof acceptService;
    readonly changeServicesPagination: typeof changeServicesPagination;
    readonly changeServiceFilters: typeof changeServiceFilters;
    readonly applyServiceFilters: typeof applyServiceFilters;
}

interface IServicesState {
    isAcceptServiceModalOpen: boolean;
    selectedServiceId: string | null;
}

class Services extends Component<IServicesProps, IServicesState> {
    constructor(props: IServicesProps) {
        super(props);

        this.state = {
            isAcceptServiceModalOpen: false,
            selectedServiceId: null,
        };
    }

    componentDidMount(): void {
        this.props.fetchServicesList(null);
    }

    componentWillUnmount() {
        this.props.resetToInitialServicePageState();
    }

    render() {
        return (
            <LayoutWrapper>
                <div className="d-flex justify-content-between align-items-center">
                    <h2 className="panel-section-header">
                        <Translation text="marketplace.services.title" />
                    </h2>
                </div>

                <Card className="services-card">
                    <CardBody>
                        <ServiceFilters changeFilters={this.changeFilters} />

                        {this.renderServiceListTable()}
                    </CardBody>
                </Card>

                <Pagination
                    listMetadata={this.props.servicesMetadata}
                    itemsPerPage={10}
                    currentPage={this.props.pagination.page}
                    changePage={this.props.changeServicesPagination}
                />

                {this.state.isAcceptServiceModalOpen ? (
                    <AcceptService
                        isModalOpen={this.state.isAcceptServiceModalOpen}
                        toggleModal={this.toggleAcceptModal}
                        serviceId={this.state.selectedServiceId}
                    />
                ) : null}

                <Loader type={LoaderType.Local} showLoader={this.props.isLoading} />
            </LayoutWrapper>
        );
    }

    private renderServiceListTable = () => {
        return (
            <Table striped responsive className="services-table">
                <thead>
                    <tr>
                        <th>
                            <Translation text="marketplace.services.table.headers.service" />
                        </th>
                        <th>
                            <Translation text="marketplace.services.table.headers.type" />
                        </th>
                        <th>
                            <Translation text="marketplace.services.table.headers.description" />
                        </th>
                        <th>
                            <Translation text="marketplace.services.table.headers.price" />
                        </th>
                        {/*There is no status returned from server*/}
                        {/*<th>*/}
                        {/*    <Translation text="marketplace.services.table.headers.status" />*/}
                        {/*</th>*/}
                        <th>
                            <Translation text="marketplace.services.table.headers.actions" />
                        </th>
                    </tr>
                </thead>
                <tbody>{this.renderServicesTableRows(this.props.servicesList)}</tbody>
            </Table>
        );
    };

    private renderServicesTableRows(services: any[] | null | undefined) {
        const {t} = this.props;

        if (!services || services.length === 0) {
            return (
                <tr key={'no-data'}>
                    <td colSpan={6}>
                        <p>
                            <Translation text="marketplace.services.table.noData" />
                        </p>
                    </td>
                </tr>
            );
        }

        return services.map((item: IModelServiceListItem) => {
            return (
                <tr key={`matches_${item.id}`}>
                    <td>
                        <p className="fw-bolder">{item.title}</p>
                    </td>
                    <td>{this.renderServiceTypes(item.types)}</td>
                    <td className="text-break">{item.description}</td>
                    <td className="fw-bolder">
                        <Price price={{amount: item.grossPrice}} />
                    </td>
                    {/*There is no status returned from server*/}
                    {/*<td>Service status</td>*/}
                    <td>
                        <div className="d-flex">
                            <Button color="flat-primary" onClick={() => this.redirectToServiceView(item)}>
                                <Search id={`view_${item.id}`} size={20} />
                                <Tooltip target={`view_${item.id}`} tooltipText={t('marketplace.services.table.tooltips.view')} />
                            </Button>

                            <Button color="flat-primary" onClick={() => this.toggleAcceptModal(item.id)}>
                                <CheckSquare id={`accept_${item.id}`} size={20} />
                                <Tooltip target={`accept_${item.id}`} tooltipText={t('marketplace.services.table.tooltips.accept')} />
                            </Button>
                        </div>
                    </td>
                </tr>
            );
        });
    }

    private renderServiceTypes = (types: typeof IModelDictionaryDatum) => {
        return types.map((item: typeof IModelDictionaryDatum) => {
            return (
                <Badge color="light-primary" pill className="mb-025" key={item.id}>
                    <span className="align-middle">
                        <Translation text={item.name} />
                    </span>
                </Badge>
            );
        });
    };

    // There is no status returned from server
    // private renderServiceStatus = (status: boolean) => {
    //     const serviceStatusTranslationKey = status
    //         ? 'marketplace.services.serviceStatus.accepted'
    //         : 'marketplace.services.serviceStatus.pending';
    //
    //     return (
    //         <Badge color={`${status ? 'light-primary' : 'light-warning'}`} pill>
    //             <Translation text={serviceStatusTranslationKey} />
    //         </Badge>
    //     );
    // };

    private redirectToServiceView = (item: any) => {
        this.props.router.navigate(`/panel/services/${item.id}`);
    };

    private toggleAcceptModal = (serviceId?: string | null) => {
        this.setState({
            isAcceptServiceModalOpen: !this.state.isAcceptServiceModalOpen,
            selectedServiceId: serviceId ? serviceId : null,
        });
    };

    private changeFilters = (value: {[key: string]: any}) => {
        this.props.changeServiceFilters(value);
        this.props.applyServiceFilters();
    };
}

export default withTranslation()(
    connect(
        (state: RootState) => ({
            isLoading: isServicesPageLoading(state),
            servicesList: servicesListSelector(state),
            serviceFilters: serviceFiltersSelector(state),
            servicesMetadata: servicesMetadataSelector(state),
            pagination: servicesPaginationSelector(state),
        }),
        {
            fetchServicesList,
            resetToInitialServicePageState,
            acceptService,
            changeServicesPagination,
            changeServiceFilters,
            applyServiceFilters,
        }
    )(withRouterWrapper(Services))
);
